/**
 * Persona Service
 *
 * API service module for audience persona management.
 */

import { apiClient, extractData } from './api';
import { AI_REQUEST_TIMEOUT } from './api/client';

// ─── Types ──────────────────────────────────────────────────────────────────

export type FunnelStage = 'tofu' | 'mofu' | 'bofu';

export interface PersonaUploader {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
}

export interface AudiencePersona {
  id: string;
  workspaceId: string;
  name: string;
  description: string | null;
  avatarUrl: string | null;
  demographics: Record<string, unknown>;
  interests: string[];
  painPoints: string[];
  goals: string[];
  funnelStage: FunnelStage;
  preferredChannels: string[];
  contentPreferences: Record<string, unknown>;
  tonePreferences: Record<string, unknown>;
  createdBy: PersonaUploader | null;
  createdAt: string;
  updatedAt: string;
}

export interface PersonasSummary {
  totalPersonas: number;
  byFunnelStage: {
    tofu: number;
    mofu: number;
    bofu: number;
  };
}

export interface ListPersonasParams {
  workspaceId: string;
  page?: number;
  limit?: number;
  search?: string;
  funnelStage?: FunnelStage;
  sortBy?: 'createdAt' | 'name' | 'funnelStage';
  sortOrder?: 'asc' | 'desc';
}

export interface ListPersonasResponse {
  personas: AudiencePersona[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  summary: PersonasSummary;
}

export interface CreatePersonaPayload {
  name: string;
  description?: string;
  avatarUrl?: string | null;
  demographics?: {
    ageRangeMin?: number | null;
    ageRangeMax?: number | null;
    gender?: string;
    location?: string;
    jobTitle?: string;
    industry?: string;
  };
  interests?: string[];
  painPoints?: string[];
  goals?: string[];
  funnelStage?: FunnelStage;
  preferredChannels?: string[];
  contentPreferences?: {
    preferredContentTypes?: string[];
    preferredChannels?: string[];
  };
  tonePreferences?: {
    brandVoiceProfileId?: string | null;
    customTone?: string;
    customFormalityLevel?: string;
    notes?: string;
  };
}

export type UpdatePersonaPayload = Partial<CreatePersonaPayload>;

export interface AISuggestedAttributes {
  suggestedDescription: string;
  suggestedDemographics: {
    ageRangeMin: number | null;
    ageRangeMax: number | null;
    gender: string;
    location: string;
    jobTitle: string;
    industry: string;
  };
  suggestedInterests: string[];
  suggestedPainPoints: string[];
  suggestedGoals: string[];
  suggestedFunnelStage: FunnelStage;
  suggestedContentTypes: string[];
  confidence: number;
  reasoning: string;
}

export interface AIGeneratePersonaRequest {
  overview: string;
  additionalContext?: string;
  brandVoiceId?: string;
}

export interface AIGeneratedPersona {
  name: string;
  description: string;
  demographics: {
    ageRangeMin: number | null;
    ageRangeMax: number | null;
    gender: string;
    location: string;
    jobTitle: string;
    industry: string;
  };
  interests: string[];
  painPoints: string[];
  goals: string[];
  funnelStage: FunnelStage;
  preferredChannels: string[];
  contentPreferences: {
    preferredContentTypes: string[];
    preferredChannels: string[];
  };
  tonePreferences: {
    brandVoiceProfileId: string | null;
    customTone: string;
    customFormalityLevel: string;
    notes: string;
  };
  knowledgeInsights: string[];
  reasoning: string;
  confidence: number;
}

export interface PersonaUsageImpact {
  campaignsUsingPersona: number;
  postsUsingPersona: number;
}

export interface DeletePersonaResponse {
  id: string;
  name: string;
  message: string;
  impactSummary: PersonaUsageImpact;
}

// ─── Helper ─────────────────────────────────────────────────────────────────

/**
 * Sanitize query params to ensure no function values are sent to the API.
 * This guards against edge cases where a String method (e.g. `.search`) is
 * accidentally passed as a parameter value.
 */
function sanitizeQueryParams(
  params: Record<string, unknown>
): Record<string, string | number | boolean | undefined> {
  const sanitized: Record<string, string | number | boolean | undefined> = {};
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === 'function') {
      // Skip function values — this is always a bug
      continue;
    }
    sanitized[key] = value as string | number | boolean | undefined;
  }
  return sanitized;
}

// ─── Persona Service ────────────────────────────────────────────────────────

export const personaService = {
  /**
   * List audience personas for a workspace.
   */
  list: async (params: ListPersonasParams): Promise<ListPersonasResponse> => {
    if (!params.workspaceId) {
      return {
        personas: [],
        pagination: {
          page: 1,
          limit: 20,
          total: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPrevPage: false,
        },
        summary: {
          totalPersonas: 0,
          byFunnelStage: { tofu: 0, mofu: 0, bofu: 0 },
        },
      };
    }

    const response = await apiClient.get(
      `/workspaces/${params.workspaceId}/personas`,
      {
        params: sanitizeQueryParams({
          page: params.page,
          limit: params.limit,
          search: params.search,
          funnelStage: params.funnelStage,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
        }),
      }
    );

    const body = response.data as {
      success: boolean;
      data: AudiencePersona[];
      meta?: {
        pagination?: ListPersonasResponse['pagination'];
        summary?: PersonasSummary;
      };
    };

    return {
      personas: body.data || [],
      pagination: body.meta?.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPrevPage: false,
      },
      summary: body.meta?.summary || {
        totalPersonas: 0,
        byFunnelStage: { tofu: 0, mofu: 0, bofu: 0 },
      },
    };
  },

  /**
   * Get a single audience persona by ID.
   */
  getById: async (workspaceId: string, personaId: string): Promise<AudiencePersona> => {
    return extractData(
      apiClient.get<AudiencePersona>(
        `/workspaces/${workspaceId}/personas/${personaId}`
      )
    );
  },

  /**
   * Create a new audience persona.
   */
  create: async (workspaceId: string, payload: CreatePersonaPayload): Promise<AudiencePersona> => {
    return extractData(
      apiClient.post<AudiencePersona>(
        `/workspaces/${workspaceId}/personas`,
        payload
      )
    );
  },

  /**
   * Update an existing audience persona.
   */
  update: async (workspaceId: string, personaId: string, payload: UpdatePersonaPayload): Promise<AudiencePersona> => {
    return extractData(
      apiClient.put<AudiencePersona>(
        `/workspaces/${workspaceId}/personas/${personaId}`,
        payload
      )
    );
  },

  /**
   * Get AI-suggested persona attributes based on workspace data (heuristic-based).
   */
  aiSuggest: async (workspaceId: string, context?: { name?: string; description?: string }): Promise<AISuggestedAttributes> => {
    return extractData(
      apiClient.post<AISuggestedAttributes>(
        `/workspaces/${workspaceId}/personas/ai-suggest`,
        context || {}
      )
    );
  },

  /**
   * Generate a complete audience persona using AI and knowledge base context (RAG-powered).
   * Uses extended timeout since AI generation can take 30-60+ seconds.
   */
  aiGenerate: async (workspaceId: string, request: AIGeneratePersonaRequest): Promise<AIGeneratedPersona> => {
    return extractData(
      apiClient.post<AIGeneratedPersona>(
        `/workspaces/${workspaceId}/personas/ai-generate`,
        request,
        { timeout: AI_REQUEST_TIMEOUT }
      )
    );
  },

  /**
   * Get usage impact for a persona (pre-delete check).
   */
  getUsageImpact: async (workspaceId: string, personaId: string): Promise<PersonaUsageImpact> => {
    return extractData(
      apiClient.get<PersonaUsageImpact>(
        `/workspaces/${workspaceId}/personas/${personaId}/usage`
      )
    );
  },

  /**
   * Delete an audience persona.
   */
  delete: async (workspaceId: string, personaId: string): Promise<DeletePersonaResponse> => {
    return extractData(
      apiClient.delete<DeletePersonaResponse>(
        `/workspaces/${workspaceId}/personas/${personaId}`
      )
    );
  },
};